/**
 * Login Form component for trAInedup.ai
 * Handles user authentication with email/password and Google OAuth
 */

import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import { Card } from '../common/Card';
import { useAuth } from '../../contexts/AuthContext';
import { ROUTES } from '../../utils/constants';
import { useAffiliateTracking } from '../../hooks/useAffiliateTracking';
import { getAuthRedirectFromSearch, resolvePostAuthRoute } from '../../utils/authRedirect';

/**
 * Login form component with validation and error handling
 */
export const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, logout, loginWithGoogleAuth, refreshUser } = useAuth();
  const { getReferralCode } = useAffiliateTracking();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  /**
   * Handle form input changes
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      setError('Please fill in all fields');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await login(formData);
      if (result.success && result.user) {
        // Refresh user data to get latest credits/subscription info
        await refreshUser();
        
        // Check email verification first
        if (!result.user.emailVerified) {
          // Sign out unverified users to prevent authenticated-but-unverified state
          await logout();
          setError('Please verify your email before signing in. Check your inbox for the verification link.');
          navigate(ROUTES.emailVerificationSent);
          return;
        }
        
        const from = (location.state as any)?.from?.pathname;
        const targetRoute = resolvePostAuthRoute({
          stateFromPathname: from,
          queryRedirect: getAuthRedirectFromSearch(location.search),
          currentPathname: location.pathname,
          fallbackRoute: ROUTES.home,
        });

        navigate(targetRoute);
      } else if (result.error) {
        setError(result.error);
      }
    } catch (error: any) {
      setError(error.message || 'Failed to sign in. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle Google OAuth login
   */
  const handleGoogleLogin = async () => {
    setIsLoading(true);
    setError('');

    try {
      const referralCode = getReferralCode();
      const result = await loginWithGoogleAuth(referralCode);
      if (result.success && result.user) {
        // Refresh user data to get latest credits/subscription info
        await refreshUser();

        // Check email verification first
        if (!result.user.emailVerified) {
          // Sign out unverified users to prevent authenticated-but-unverified state
          await logout();
          setError('Please verify your email before signing in. Check your inbox for the verification link.');
          navigate(ROUTES.emailVerificationSent);
          return;
        }

        const from = (location.state as any)?.from?.pathname;
        const targetRoute = resolvePostAuthRoute({
          stateFromPathname: from,
          queryRedirect: getAuthRedirectFromSearch(location.search),
          currentPathname: location.pathname,
          fallbackRoute: ROUTES.home,
        });

        navigate(targetRoute);
      } else if (result.error) {
        setError(result.error);
      }
    } catch (error: any) {
      setError(error.message || 'Failed to sign in with Google. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <div className="text-center mb-8">
        <h2 className="font-montserrat font-bold text-2xl text-neutral-800 mb-2">
          Welcome Back
        </h2>
        <p className="text-neutral-600">
          Sign in to continue your AI learning journey
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      {/* Login Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <Input
          label="Email Address"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleInputChange}
          icon={Mail}
          placeholder="Enter your email"
          required
          disabled={isLoading}
          fullWidth
        />

        <Input
          label="Password"
          name="password"
          type={showPassword ? 'text' : 'password'}
          value={formData.password}
          onChange={handleInputChange}
          icon={Lock}
          placeholder="Enter your password"
          required
          disabled={isLoading}
          fullWidth
          rightAction={
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="text-neutral-400 hover:text-neutral-600 transition-colors duration-200"
              disabled={isLoading}
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          }
        />

        <div className="flex items-center justify-between">
          <label className="flex items-center">
            <input
              type="checkbox"
              className="rounded border-neutral-300 text-primary focus:ring-primary-500"
              disabled={isLoading}
            />
            <span className="ml-2 text-sm text-neutral-600">Remember me</span>
          </label>
          
          <Link
            to={ROUTES.forgotPassword}
            className="text-sm text-primary hover:text-primary-600 transition-colors duration-200"
          >
            Forgot password?
          </Link>
        </div>

        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          loading={isLoading}
          disabled={isLoading}
        >
          {isLoading ? 'Signing In...' : 'Sign In'}
        </Button>
      </form>

      {/* Divider */}
      <div className="my-6 flex items-center">
        <div className="flex-1 border-t border-neutral-200"></div>
        <span className="px-4 text-sm text-neutral-500">or</span>
        <div className="flex-1 border-t border-neutral-200"></div>
      </div>

      {/* Google Login */}
      <Button
        variant="outline"
        size="lg"
        fullWidth
        onClick={handleGoogleLogin}
        disabled={isLoading}
        className="mb-6"
      >
        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
          <path
            fill="currentColor"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="currentColor"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="currentColor"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="currentColor"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
        Continue with Google
      </Button>

      {/* Sign Up Link */}
      <div className="text-center">
        <p className="text-neutral-600">
          Don't have an account?{' '}
          <Link
            to={ROUTES.register}
            state={location.state}
            className="text-primary hover:text-primary-600 font-medium transition-colors duration-200"
          >
            Sign up for free
          </Link>
        </p>
      </div>
    </Card>
  );
};

